import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { DropdownItem } from "reactstrap";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Dropdown from "@components/Dropdown";
import Avatar from "@components/Avatar";
import Modal from "@components/Modal";
import ReportModal from "@components/ReportModal";
import VerifiedUserIcon from "../VerifiedUserIcon";
import { getScheduledDateString } from "@helpers/dates";
import { useService } from "@/base/hooks/useService";
import { deletePost, pinPost, unpinPost } from "@redux/posts/actions";
import { deleteScheduledPost } from "@redux/scheduledPosts/actions";
import { reportPost } from "@redux/report/actions";
import { localstorageKeys } from "@const/store";
import formatWithLocale from "@helpers/formatWithLocale";

import { ReactComponent as IconShowMore } from "@assets/ic-show-more.svg";
import { ReactComponent as IconPinned } from "@assets/ic_pin.svg";

import { StorageService } from "@services/StorageService";

import styles from "./index.module.scss";
import { toggleSignInModal } from "@redux/signIn/actions";
import routes from "../../const/routes";

const PostHeader = ({
  author,
  postId,
  toggleModal,
  avatarContainerClassName,
  avatarClassName,
  wrapperClassName,
  publishedDate,
  isLocked,
  accessToken,
  uniqueKey,
  isPinned,
  isFeedPost,
  isScheduled,
  scheduledDate,
  isPinnedOptionVisible = true,
  stream,
}) => {
  const { t } = useTranslation("buttons");
  const { t: t1 } = useTranslation("my-profile");
  const { t: t2 } = useTranslation("common");
  const { t: t3 } = useTranslation("livestream");

  const dispatch = useDispatch();
  const storageService = useService(StorageService);
  const [postToDelete, setPostToDelete] = useState(null);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const navigate = useNavigate();

  const { allPosts, postById } = useSelector(
    ({ postsReducer }) => postsReducer,
  );

  const handleClose = () => setPostToDelete(null);

  const handleEditClick = (e) => {
    e?.stopPropagation();
    navigate(`/cp/${uniqueKey}/edit`, {
      state: { from: location.pathname },
    });
  };

  const toggleReport = (e) => {
    e?.stopPropagation();
    if (accessToken) {
      setIsReportOpen((state) => !state);
      return;
    }
    storageService.set(
      localstorageKeys.destination,
      `${routes.CREATOR_PROFILE()}${encodeURIComponent(author?.username)}`,
    );
    dispatch(toggleSignInModal(true));
  };

  const handleConfirm = useCallback(() => {
    if (isScheduled) {
      dispatch(deleteScheduledPost({ id: postToDelete }));
    } else {
      dispatch(
        deletePost({
          id: postToDelete,
        }),
      );
    }

    if (toggleModal) {
      toggleModal();
    }
  }, [postToDelete, toggleModal, isScheduled]);

  const handlePinning = useCallback(
    (e) => {
      e?.stopPropagation();
      if (isPinned) return dispatch(unpinPost({ id: postId }));

      const isFirstPostPinned = postById[allPosts[0]].isPinned;

      if (isFirstPostPinned)
        return dispatch(pinPost({ unpinId: allPosts[0], pinId: postId }));

      dispatch(
        pinPost({
          pinId: postId,
        }),
      );
    },
    [dispatch, postId, allPosts, postById],
  );

  const { isDeleting } = useSelector(
    ({ postsReducer, scheduledPostsReducer }) =>
      isScheduled ? scheduledPostsReducer : postsReducer,
  );

  const { isPinning } = useSelector(({ postsReducer }) => postsReducer);

  const { uniqueKey: myKey, avatar } = useSelector(
    ({ profileReducer }) => profileReducer,
  );

  const isMyPost = author?.uniqueKey === myKey;

  return (
    <div className={classNames("d-flex", wrapperClassName)}>
      <Modal
        buttonsDisabled={isDeleting}
        unmountOnClose
        isOpen={!!postToDelete}
        toggle={handleClose}
        onClose={handleClose}
        onCancel={handleClose}
        onConfirm={handleConfirm}
        title={t1(isScheduled ? "DELETE_SCHEDULED_POST" : "DELETE_POST")}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        dialogClassName="modal-dialog-centered"
        className={styles.modalContainer}
        cancelClassName={classNames(styles.modalButton, "mb-2 mb-xl-0")}
        confirmClassName={classNames(styles.modalButton, "ms-0 ms-xl-1")}
      >
        <div className="text-primary text-center text-body-1 text-regular">
          {t1(isScheduled ? "DELETE_SCHEDULED_POST_SURE" : "DELETE_POST_SURE")}
        </div>
      </Modal>
      <div
        className={classNames(
          "d-flex w-100 position-relative align-items-center",
          styles.avatarContainer,
          avatarContainerClassName,
        )}
      >
        {!isScheduled ? (
          <>
            <Link
              onClick={(e) => e.stopPropagation()}
              to={`${routes.CREATOR_PROFILE()}${author?.username}`}
            >
              <Avatar
                avatar={isMyPost ? avatar : author.avatar}
                isDeleted={author?.isDeleted}
                className={classNames(styles.headerAvatar, avatarClassName)}
              />
            </Link>
            <div className="w-100">
              <div className="d-flex align-items-center mb-1">
                <Link
                  onClick={(e) => e.stopPropagation()}
                  to={`${routes.CREATOR_PROFILE()}${author?.username}`}
                  className="text-decoration-none d-flex fit-content"
                >
                  <p className="notranslate mb-0 text-break text-body-1 text-semibold text-primary d-flex fit-content">
                    {author.username}
                  </p>
                </Link>
                <VerifiedUserIcon
                  isVisible={author?.isIdentityVerified}
                  className="ms-2"
                />
              </div>

              {stream ? (
                <div className="d-flex flex-column px-2 px-xl-0 py-2 py-xl-0">
                  <span className="text-secondary text-body-2">
                    {t3("STREAMED_ON")}{" "}
                    {formatWithLocale(
                      new Date(stream.createdAt),
                      "dd MMM yyyy",
                    )}
                  </span>
                </div>
              ) : null}

              {publishedDate && !stream && (
                <p className="mb-0 text-body-2 text-regular text-secondary">
                  {formatWithLocale(new Date(publishedDate), "dd MMM y, HH:mm")}
                </p>
              )}
            </div>
          </>
        ) : (
          <div>
            <div className="text-body-2 text-secondary">
              {t2("SCHEDULED_FOR_POSTING")}
            </div>
            <div className="text-body-2 text-body-1-xl text-primary">
              {getScheduledDateString(scheduledDate)}
            </div>
          </div>
        )}

        <div className="d-flex align-items-center">
          {isPinned && !isFeedPost && (
            <IconPinned className={styles.pinnedIcon} />
          )}
          {!isLocked && (
            <Dropdown
              end
              toggler={<IconShowMore />}
              togglerClassName={classNames("btn-resetStyle", styles.toggler)}
              menuClassName={classNames("w-auto", styles.dropdownMenu)}
              wrapperClassName={classNames(
                styles.dropdown,
                isScheduled && styles.scheduledDropdown,
              )}
            >
              {author.uniqueKey === myKey ? (
                <>
                  {isPinnedOptionVisible && !isScheduled && (
                    <DropdownItem onClick={handlePinning} disabled={isPinning}>
                      {t1(isPinned ? "UNPIN_POST" : "PIN_POST")}
                    </DropdownItem>
                  )}
                  <DropdownItem onClick={handleEditClick} disabled={isDeleting}>
                    {t1("EDIT_POST")}
                  </DropdownItem>
                  <DropdownItem
                    disabled={isDeleting}
                    onClick={(e) => {
                      e?.stopPropagation();
                      setPostToDelete(postId);
                    }}
                  >
                    {t1("DELETE_POST")}
                  </DropdownItem>
                </>
              ) : (
                <DropdownItem onClick={toggleReport}>
                  {t2("REPORT")}
                </DropdownItem>
              )}
            </Dropdown>
          )}
        </div>
      </div>
      <ReportModal
        action={reportPost}
        id={postId}
        isOpen={isReportOpen}
        toggle={toggleReport}
        footerClassName="flex-column flex-xl-row align-items-stretch align-items-xl-start"
      />
    </div>
  );
};

export default PostHeader;
